import { z } from 'zod';

import { RoleSchema } from './enums.js';
import { EmailSchema, UuidSchema } from './primitives.js';

/**
 * A Help button report (issue #175, phase 1).
 *
 * The object is an allowlist, and it is strict: a child's roster, allergy,
 * medical, or photo fields are not optional extras that get ignored, they
 * fail the request. The server stores only what this schema names.
 */

export const SUPPORT_REPORT_BODY_MAX = 2000;

/** What a person can file by hand. */
export const SupportManualKindSchema = z.enum(['bug', 'feature']);
/** What a stored report can be: also `auto_error`, filed by the app itself. */
export const SupportReportKindSchema = z.enum(['bug', 'feature', 'auto_error']);
export type SupportReportKind = z.infer<typeof SupportReportKindSchema>;

export const SupportReportDeviceSchema = z.enum(['phone', 'tablet', 'desktop', 'unknown']);
export type SupportReportDevice = z.infer<typeof SupportReportDeviceSchema>;

/** `H` plus five characters from the support-code alphabet. Shown to the reporter. */
export const SupportReportReferenceSchema = z.string().regex(/^H[ACDEFGHJKMNPQRTUVWXY34679]{5}$/);

/**
 * "What is this about?" The one list the form, the API check, the admin screen
 * and the GitHub label all read. `area` is the label suffix (`area:schedule`).
 */
export const SUPPORT_CATEGORIES = [
  { id: 'account', label: 'Sign-in and account', hint: 'Logging in, passwords, your profile' },
  { id: 'permissions', label: 'Permissions and who can see what', hint: 'Roles, access, privacy' },
  { id: 'schedule', label: 'Schedule and calendar', hint: 'Games, practices, calendar feeds' },
  { id: 'scorebook', label: 'Live scorebook', hint: 'Scoring a game as it happens' },
  { id: 'stats', label: 'Stats and box scores', hint: 'Numbers, totals, box scores' },
  { id: 'roster', label: 'Roster and players', hint: 'Players, jerseys, positions' },
  { id: 'families', label: 'Families and invites', hint: 'Parents, guardians, invitations' },
  { id: 'messages', label: 'Announcements and messages', hint: 'Team news, replies' },
  {
    id: 'club-setup',
    label: 'Divisions, leagues and club setup',
    hint: 'Seasons, divisions, teams',
  },
  { id: 'notifications', label: 'Notifications and email', hint: 'Reminders, emails, alerts' },
  { id: 'offline', label: 'Offline and syncing', hint: 'Saved on the device, not arriving' },
  { id: 'other', label: 'Something else', hint: 'Anything that does not fit above' },
] as const;

export type SupportCategoryId = (typeof SUPPORT_CATEGORIES)[number]['id'];

export const SupportCategoryIdSchema = z.enum([
  'account',
  'permissions',
  'schedule',
  'scorebook',
  'stats',
  'roster',
  'families',
  'messages',
  'club-setup',
  'notifications',
  'offline',
  'other',
] satisfies readonly SupportCategoryId[]);

export function supportCategory(id: string): (typeof SUPPORT_CATEGORIES)[number] | undefined {
  return SUPPORT_CATEGORIES.find((category) => category.id === id);
}

/** The GitHub label for a category, e.g. `area:schedule`. */
export function supportAreaLabel(id: SupportCategoryId): string {
  return `area:${id}`;
}

/** 1 Nice to have, 2 Would help, 3 Really need this. Feature requests only. */
export const SupportImportanceSchema = z.union([z.literal(1), z.literal(2), z.literal(3)]);
export type SupportImportance = z.infer<typeof SupportImportanceSchema>;
export const SUPPORT_IMPORTANCE_LABELS: Record<SupportImportance, string> = {
  1: 'Nice to have',
  2: 'Would help',
  3: 'Really need this',
};

export const SUPPORT_TITLE_MAX = 120;
export const SUPPORT_CONSOLE_ERRORS_MAX = 10;
export const SUPPORT_CONSOLE_ERROR_CHARS = 300;

/** What the form shows the person before it sends. Nothing here is read from a roster. */
export const SupportDiagnosticsSchema = z
  .object({
    browser: z.string().trim().max(120),
    online: z.boolean(),
    language: z.string().trim().max(20).optional(),
    consoleErrors: z
      .array(z.string().max(SUPPORT_CONSOLE_ERROR_CHARS))
      .max(SUPPORT_CONSOLE_ERRORS_MAX),
  })
  .strict();
export type SupportDiagnostics = z.infer<typeof SupportDiagnosticsSchema>;

export const SUPPORT_SCREENSHOT_MIMES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export type SupportScreenshotMime = (typeof SUPPORT_SCREENSHOT_MIMES)[number];
/** Decoded size cap. The API checks the bytes, not the claim. */
export const SUPPORT_SCREENSHOT_MAX_BYTES = 2 * 1024 * 1024;
/** base64 of the cap, plus slack for padding. */
export const SUPPORT_SCREENSHOT_MAX_BASE64 = Math.ceil((SUPPORT_SCREENSHOT_MAX_BYTES * 4) / 3) + 4;

export const SupportScreenshotSchema = z
  .object({
    mime: z.enum(SUPPORT_SCREENSHOT_MIMES),
    data: z
      .string()
      .min(8)
      .max(SUPPORT_SCREENSHOT_MAX_BASE64)
      .regex(/^[A-Za-z0-9+/]+={0,2}$/),
  })
  .strict();
export type SupportScreenshot = z.infer<typeof SupportScreenshotSchema>;

export const SupportReportRequestSchema = z
  .object({
    clientId: UuidSchema.transform((value) => value.toLowerCase()),
    kind: SupportManualKindSchema,
    title: z.string().trim().min(1).max(SUPPORT_TITLE_MAX).optional(),
    category: SupportCategoryIdSchema.optional(),
    importance: SupportImportanceSchema.optional(),
    diagnostics: SupportDiagnosticsSchema.optional(),
    screenshot: SupportScreenshotSchema.optional(),
    body: z.string().trim().min(1).max(SUPPORT_REPORT_BODY_MAX),
    contactEmail: EmailSchema.optional(),
    role: RoleSchema.optional(),
    route: z.string().trim().min(1).max(300),
    device: SupportReportDeviceSchema,
    appVersion: z.string().trim().min(1).max(40),
    viewport: z.string().trim().min(1).max(20),
    supportCode: z.string().trim().min(4).max(12).optional(),
  })
  .strict();
export type SupportReportRequest = z.infer<typeof SupportReportRequestSchema>;

/** Where an automatic report came from. `server` is only ever set by the API itself. */
export const SUPPORT_AUTO_SOURCES = ['window', 'rejection', 'boundary', 'api', 'network'] as const;
export const SupportAutoSourceSchema = z.enum(SUPPORT_AUTO_SOURCES);
export type SupportAutoSource = z.infer<typeof SupportAutoSourceSchema>;

export const SUPPORT_AUTO_MESSAGE_MAX = 500;
export const SUPPORT_AUTO_STACK_MAX = 1500;
export const SUPPORT_DETAILS_MAX = 1000;

/**
 * An error the app caught on its own and is reporting for the person.
 *
 * The same allowlist rule as a typed note: strict, nothing about a roster. The
 * server computes the fingerprint itself, so a client cannot aim its report at
 * somebody else's ticket.
 */
export const SupportAutoErrorRequestSchema = z
  .object({
    clientId: UuidSchema.transform((value) => value.toLowerCase()),
    source: SupportAutoSourceSchema,
    name: z.string().trim().min(1).max(80),
    message: z.string().max(SUPPORT_AUTO_MESSAGE_MAX),
    stack: z.string().max(SUPPORT_AUTO_STACK_MAX).optional(),
    route: z.string().trim().min(1).max(300),
    device: SupportReportDeviceSchema,
    appVersion: z.string().trim().min(1).max(40),
    viewport: z.string().trim().min(1).max(20),
    diagnostics: SupportDiagnosticsSchema.optional(),
    diagnosticCode: z.string().trim().min(4).max(12).optional(),
    apiStatus: z.number().int().min(100).max(599).optional(),
    apiPath: z.string().trim().max(120).optional(),
  })
  .strict();
export type SupportAutoErrorRequest = z.infer<typeof SupportAutoErrorRequestSchema>;

/** The reference, a token only this device holds, and how many times this error has been seen. */
export const SupportAutoErrorAcceptedSchema = z.object({
  reference: SupportReportReferenceSchema,
  followupToken: z.string().regex(/^[A-Za-z0-9_-]{32,64}$/),
  occurrences: z.number().int().min(1),
});
export type SupportAutoErrorAccepted = z.infer<typeof SupportAutoErrorAcceptedSchema>;

/** A note added to an automatic report afterwards, proven by the token returned when it was filed. */
export const SupportDetailsRequestSchema = z
  .object({
    token: z.string().regex(/^[A-Za-z0-9_-]{32,64}$/),
    note: z.string().trim().min(1).max(SUPPORT_DETAILS_MAX),
  })
  .strict();
export type SupportDetailsRequest = z.infer<typeof SupportDetailsRequestSchema>;

/**
 * A note on a ticket the API filed itself for a failed call. The token came back in the failed
 * reply's `X-Support-Followup` header; it alone finds the ticket and proves the right to write.
 */
export const SupportDetailsByTokenRequestSchema = SupportDetailsRequestSchema;

export const SupportReportAcceptedSchema = z.object({
  reference: SupportReportReferenceSchema,
  /** False when a screenshot was sent and could not be kept. The report itself is filed. */
  screenshotSaved: z.boolean().optional(),
});
export type SupportReportAccepted = z.infer<typeof SupportReportAcceptedSchema>;

/**
 * The category an automatic report is filed under, from the screen or API path
 * it happened on. A guess, and the admin can see the path. `other` when no
 * prefix matches.
 */
export function supportCategoryForPath(path: string): SupportCategoryId {
  const bare = path.replace(/^.*?\/api(?=\/)/i, '').toLowerCase();
  const rules: readonly (readonly [RegExp, SupportCategoryId])[] = [
    [/^\/(boxscore|stats)|\/boxscore/, 'stats'],
    [/^\/scorebook/, 'scorebook'],
    [/^\/(signin|account|auth|welcome|password)/, 'account'],
    [/^\/(schedule|calendar|rsvp|events?)/, 'schedule'],
    [/^\/(roster|allergy-card|photos?|players?)/, 'roster'],
    [/^\/(household|invitations|join|guardians?|family|invite)/, 'families'],
    [/^\/(announcements|communication|messages|threads)/, 'messages'],
    [/^\/(admin|club|leagues?|divisions?|onboarding|org)/, 'club-setup'],
    [/^\/(notifications|push)/, 'notifications'],
    [/^\/team/, 'roster'],
  ];
  for (const [pattern, id] of rules) if (pattern.test(bare)) return id;
  return 'other';
}
